import { ReactElement, ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useLocation, useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { ListElement, SpinnerLegacy } from "../../../../components";
import { useDashboardContext } from "../../../../components/dashboard/Dashboard.shared";
import { InfiniteList } from "../../../../components/infiniteLoading/InfiniteList";
import {
  ACCOUNT,
  ACCOUNT_SETTINGS,
  BUSINESS,
  BUSINESS_SETTINGS,
  NEW_PRODUCT,
  PRODUCT_LIST_CATEGORY,
} from "../../../../constants/routes";
import { useParamAsNumber } from "../../../../hooks/useParamAsNumber";
import { useProductList } from "../../../../hooks/api/v2/product/useProductList";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { ProductCategoryHeader } from "./ProductCategoryHeader";
import { GroupProductsBanner } from "../../../../components/groupProductsBanner/GroupsProductsBanner";

export function ProductListV2({
  active,
  searchValue,
}: {
  active: boolean;
  searchValue?: string;
}): ReactElement {
  const [showGrouping, setShowGrouping] = useState(true);
  const [searchParams] = useSearchParams();
  const productList = useProductList({
    limit: 10,
    sortBy: "name",
    sortDir: "asc",
    search: searchValue,
    query: [
      {
        type: "ParentId",
        modifier: "IS_NULL",
      },
      {
        type: "type",
        modifier: "EQUALS",
        value: "product",
      },
      {
        type: "active",
        modifier: "EQUALS",
        value: active,
      },
      {
        type: "showAllProducts",
        modifier: "EQUALS",
        value: true,
      },
    ],
  });

  const { business } = useSellarCurrentBusiness();

  {
    if (!business?.Supplier) {
      throw new Error("Page only accessible to suppliers.");
    }
  }

  const productId = useParamAsNumber(PRODUCT_LIST_CATEGORY, "productId", false);
  const location = useLocation();
  const { isTwoPanel } = useDashboardContext();

  const navigate = useNavigate();

  useEffect(() => {
    if (
      isTwoPanel &&
      productList.data?.pages[0]?.length &&
      !productId &&
      location.pathname !== NEW_PRODUCT
    ) {
      //if two panel layout and no orders selected, automatically select first order

      const firstProduct = productList.data.pages[0].find(
        (product) => product.type !== "category",
      );

      if (firstProduct) {
        navigate(PRODUCT_LIST_CATEGORY.href({ productId: firstProduct.id }), {
          replace: true,
        });
      }
    }
  }, [
    isTwoPanel,
    productId,
    productList.data?.pages,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    if (isTwoPanel && location.pathname === BUSINESS) {
      navigate(BUSINESS_SETTINGS);
    }
    if (isTwoPanel && location.pathname === ACCOUNT) {
      navigate(ACCOUNT_SETTINGS);
    }
  }, [location.pathname, isTwoPanel, navigate]);
  return (
    <>
      {productList.isLoading ? (
        <SpinnerLegacy />
      ) : (
        <>
          {showGrouping && (
            <div className="m-2">
              <GroupProductsBanner
                onClickClose={() => setShowGrouping(false)}
              />
            </div>
          )}
          <InfiniteList query={productList}>
            {productList.data?.pages.map((page) =>
              page.map((product) => (
                <ProductListElement
                  id={product.id}
                  key={product.id}
                  to={`${PRODUCT_LIST_CATEGORY.href({
                    productId: product.id,
                  })}?tab=${searchParams.get("tab")}`}
                >
                  <ProductCategoryHeader
                    imageUrl={
                      product.imageUrl ?? business.Supplier?.imageUrl ?? ""
                    }
                    name={product.name}
                    kind={product.kind}
                    abv={product.abv}
                  />
                </ProductListElement>
              )),
            )}
          </InfiniteList>
        </>
      )}
    </>
  );
}

const ProductListElement = ({
  id,
  to,
  children,
  className,
}: {
  id: number;
  to: string;
  children: ReactNode;
  className?: string;
}) => {
  const productId = useParamAsNumber(PRODUCT_LIST_CATEGORY, "productId", false);

  return (
    <ListElement
      to={to}
      className={twMerge(className)}
      selected={id === productId}
    >
      {children}
    </ListElement>
  );
};
